import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';

import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useRealtime } from '../../contexts/RealtimeContext';

function diffDays(a, b) {
  const d1 = new Date(a);
  const d2 = new Date(b);
  return Math.round(Math.abs((d2 - d1) / (1000 * 60 * 60 * 24)));
}

const STATUS_STYLES = {
  'active-reservation': 'text-amber-600 bg-amber-50 dark:bg-amber-900/30',
  'confirmed-stay': 'text-green-600 bg-green-50 dark:bg-green-900/30',
  'completed-stay': 'text-blue-600 bg-blue-50 dark:bg-blue-900/30',
};

const STATUS_LABELS = {
  'active-reservation': 'Reserved',
  'confirmed-stay': 'Active Stay',
  'completed-stay': 'Completed',
};

const FIN_STYLES = {
  covered_by_senghor: 'text-green-600 bg-green-50 dark:bg-green-900/30',
  not_covered_by_senghor: 'text-slate-500 bg-slate-100 dark:bg-slate-800',
};

const FIN_LABELS = {
  covered_by_senghor: 'Covered',
  not_covered_by_senghor: 'Not Covered',
};

export default function StayHistory() {
  const { user } = useAuth();
  const { isAdmin, isViewer } = useRBAC();
  const { stays, residents } = useRealtime();
  const isAdminOrViewer = isAdmin || isViewer;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBuilding, setFilterBuilding] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // For guests: find their stays by email
  const currentResident = residents?.find(r => r?.email === user?.email);

  const myStays = isAdminOrViewer
    ? stays
    : stays?.filter(s => s?.guest_id === user?.id || s?.resident_id === currentResident?.id || s?.email === user?.email);

  const filtered = (myStays || [])?.filter(s => {
    const matchStatus = filterStatus === 'all' || s?.status === filterStatus;
    const matchBuilding = filterBuilding === 'all' || s?.building === filterBuilding;
    const matchSearch = !searchQuery ||
      s?.resident_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      s?.room_number?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      s?.building?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchStatus && matchBuilding && matchSearch;
  })?.sort((a, b) => {
    if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === 'longest') return diffDays(b?.check_in_date, b?.check_out_date) - diffDays(a?.check_in_date, a?.check_out_date);
    return 0;
  });

  const buildings = [...new Set((myStays || []).map(s => s.building).filter(Boolean))];

  const stats = {
    total: myStays?.length || 0,
    completed: myStays?.filter(s => s?.status === 'completed-stay')?.length || 0,
    active: myStays?.filter(s => s?.status === 'confirmed-stay')?.length || 0,
    totalNights: (myStays || [])?.reduce((acc, s) => acc + (s?.check_in_date && s?.check_out_date ? diffDays(s?.check_in_date, s?.check_out_date) : 0), 0),
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isAdminOrViewer ? 'Stay History' : 'My Stay History'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isAdminOrViewer ? 'Complete historical records of all resident stays' : 'View your previous and current stays'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Stays', value: stats?.total, icon: 'History', color: 'text-blue-500' },
            { label: 'Completed', value: stats?.completed, icon: 'CheckCircle', color: 'text-green-500' },
            { label: 'Active', value: stats?.active, icon: 'DoorOpen', color: 'text-amber-500' },
            { label: 'Total Nights', value: stats?.totalNights, icon: 'Moon', color: 'text-purple-500' },
          ]?.map(s => (
            <div key={s?.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={s?.icon} size={16} className={s?.color} />
                <span className="text-xs text-muted-foreground font-medium">{s?.label}</span>
              </div>
              <div className="text-2xl font-bold text-foreground">{s?.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          {isAdminOrViewer && (
            <div className="relative">
              <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                placeholder="Search residents..."
                className="pl-8 pr-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-48"
              />
            </div>
          )}
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none">
            <option value="all">All Statuses</option>
            <option value="active-reservation">Reserved</option>
            <option value="confirmed-stay">Active Stay</option>
            <option value="completed-stay">Completed</option>
          </select>
          {buildings?.length > 0 && (
            <select value={filterBuilding} onChange={e => setFilterBuilding(e?.target?.value)} className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none">
              <option value="all">All Buildings</option>
              {buildings?.map(b => <option key={b} value={b}>Building {b}</option>)}
            </select>
          )}
          <select value={sortBy} onChange={e => setSortBy(e?.target?.value)} className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="longest">Longest Stay</option>
          </select>
        </div>

        {/* Stay List */}
        {filtered?.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="History" size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No stay history found</p>
            <p className="text-sm mt-1">{!isAdminOrViewer ? 'Your stay history will appear here once you have a reservation.' : 'No stays match your filters.'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered?.map(stay => {
              const nights = stay?.check_in_date && stay?.check_out_date ? diffDays(stay?.check_in_date, stay?.check_out_date) : null;
              return (
                <div key={stay?.id} className="bg-card border border-border rounded-xl p-4 hover:border-primary/20 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {isAdminOrViewer && (
                        <div className="text-sm font-semibold text-foreground mb-1">{stay?.resident_name}</div>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES?.[stay?.status] || 'text-slate-500 bg-slate-100'}`}>
                          {STATUS_LABELS?.[stay?.status] || stay?.status}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${FIN_STYLES?.[stay?.financial_status] || 'text-slate-500 bg-slate-100'}`}>
                          {FIN_LABELS?.[stay?.financial_status] || stay?.financial_status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="text-muted-foreground mb-0.5">Building / Room</div>
                          <div className="font-medium text-foreground">{stay?.building} · {stay?.room_number}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground mb-0.5">Arrival</div>
                          <div className="font-medium text-foreground">{stay?.check_in_date ? new Date(stay.check_in_date)?.toLocaleDateString() : '—'}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground mb-0.5">Departure</div>
                          <div className="font-medium text-foreground">{stay?.check_out_date ? new Date(stay.check_out_date)?.toLocaleDateString() : '—'}</div>
                        </div>
                        {nights !== null && (
                          <div>
                            <div className="text-muted-foreground mb-0.5">Duration</div>
                            <div className="font-medium text-foreground">{nights} night{nights !== 1 ? 's' : ''}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground flex-shrink-0">
                      {new Date(stay.created_at)?.toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
